// @flow strict

import type { Player } from "~robin-hood/utils";
import type { UnixTime } from "~common/utils";
import { PLAYERS } from "~robin-hood/utils";

export const GROUP_CREATOR: "0" = "0";
export type Agent = typeof GROUP_CREATOR | "1" | "2" | "3" | "4" | "5";

export type Color = number;
export type ColoresRow = string;

export type Colores = [ColoresRow, ColoresRow, ColoresRow];

export type CardSuit = "rombos" | "corazones" | "picas" | "treboles";
export type CardValue =
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "J"
  | "Q"
  | "K"
  | "A";
export opaque type Card = string; // "${CardValue}|${CardSuit}"

export function getCardSuit(card: Card): CardSuit {
  // $FlowFixMe[incompatible-return]
  return card.split("|")[1];
}
export function getCardValue(card: Card): CardValue {
  // $FlowFixMe[incompatible-return]
  return card.split("|")[0];
}
export function asCard(value: CardValue, suit: CardSuit): Card {
  return `${value}|${suit}`;
}
export type PokerValue = [Card, Card, Card];
export type SonidoIdx = 0 | 1 | 2 | 3;
export type Sonidos = [number, number, number, number];

export type Casillas = string;
export type Ahorcado = string;
type Pruebas = {
  consola?: { value: string, correct: boolean },
  digitos?: { value: [number, number, number], correct: boolean },
  ahorcado?: { value: Ahorcado, correct: boolean },
  poker?: { value: PokerValue, correct: boolean },
  colores?: { value: Colores, correct: boolean },
  casillas?: { value: Casillas, correct: boolean },
  sonidos?: { value: Sonidos, correct: boolean },
};

export type Equipo = $ReadOnly<{
  id: string,
  name: string,
  players: number,
  // agents: Array<Agent>,
  //
  startTime?: ?UnixTime,
  endTime?: UnixTime,
  extraTimeStart?: UnixTime,
  // We store here the correct values received from server
  pruebas?: Pruebas,
}>;

export type LocalCasillas = {
  value?: Casillas,
  selected?: Array<string>,
};

export type LocalColores = {
  value?: Colores,
  selected?: Array<string>,
};

export type LocalDigitos = {
  value?: [number, number, number],
};

export type LocalPoker = {
  value?: PokerValue,
};
export type LocalSonidos = {
  value?: Sonidos,
};
export type LocalPruebas = {
  inicial?: {
    [Player]: { value: string, correct: boolean },
  },
  consola?: { [Player]: { value: string } },
  digitos?: { [Player]: LocalDigitos },
  ahorcado?: { [Player]: { value: Ahorcado } },
  poker?: { [Player]: LocalPoker },
  colores?: { [Player]: LocalColores },
  casillas?: { [Player]: LocalCasillas },
  sonidos?: { [Player]: LocalSonidos },
};

const AGENTS = ["0", "1", "2", "3", "4", "5"];

export type RegisteredStore = $ReadOnly<{
  equipo: Equipo,
  agent: Agent,
  // We store here the local values for each player
  local?: LocalPruebas,
}>;

export type UnregisteredStore = $ReadOnly<{
  equipo?: void,
  hasSeenInitialText?: boolean,
}>;

export type Store = $ReadOnly<RegisteredStore | UnregisteredStore>;

export function agentsMap<T>(values: [T, T, T, T, T, T]): Map<Agent, T> {
  return new Map(AGENTS.map((p, idx) => [p, values[idx]]));
}

export const AGENT_GATO = "000";
export const AGENT_NAME: Map<Agent, string> = agentsMap([
  "001",
  "002",
  "003",
  "004",
  "005",
  "006",
]);

export function equipoWithPrueba(
  equipo: Equipo,
  pruebas: $Shape<Pruebas>
): Equipo {
  return {
    ...equipo,
    pruebas: {
      ...equipo.pruebas,
      ...pruebas,
    },
  };
}

export function localStoreWithPruebas(
  store: RegisteredStore,
  prueba: $Shape<LocalPruebas>
): RegisteredStore {
  return {
    ...store,
    local: {
      ...store.local,
      ...prueba,
    },
  };
}

export function localStoreWithPrueba<
  K: $Keys<LocalPruebas>,
  V: $ElementType<LocalPruebas, K>
>(store: RegisteredStore, prueba: K, value: V): RegisteredStore {
  return deepCopyWithValues(store, {
    local: {
      [(prueba: string)]: value,
    },
  });
}

// Creates a new object replacing or creating the new props indicated in merge.
// To save memory, IT DOES MODIFY the 'merge' parameter so don't reuse that object anywhere else.
export function deepCopyWithValues<T: $FlowFixMeObjectType>(
  object: T,
  merge: $Shape<T>
): T {
  Object.keys(merge).forEach((key) => {
    const merged = merge[key];
    if (object.hasOwnProperty(key)) {
      const value = object[key];
      if (isPlainObject(merged) && isPlainObject(value)) {
        merge[key] = deepCopyWithValues(value, merged);
      }
    }
  });

  return { ...object, ...merge };
}

function isPlainObject(obj: $ReadOnly<{}>): boolean {
  if (typeof obj !== "object" || obj == null) return false;

  const proto = Object.getPrototypeOf(obj);
  return proto !== null && Object.getPrototypeOf(proto) === null;
}

export function knowsPlayerSecret(
  store: RegisteredStore,
  player: Player
): boolean {
  const inicial = store.local?.inicial;
  return inicial && inicial[player] ? inicial[player].correct : false;
}

export function getAgentPlayers(equipo: Equipo, agent: Agent): Array<Player> {
  switch (equipo.players) {
    case 2:
      return agent === "0" ? PLAYERS.slice(0, 3) : PLAYERS.slice(3, 6);
    case 3:
      switch (agent) {
        case "0":
          return PLAYERS.slice(0, 2);
        case "1":
          return PLAYERS.slice(2, 4);
        case "2":
          return PLAYERS.slice(4, 6);
        default:
          throw Error();
      }
    case 4:
      switch (agent) {
        case "0":
          return PLAYERS.slice(0, 2);
        case "1":
          return PLAYERS.slice(2, 4);
        case "2":
          return PLAYERS.slice(4, 5);
        case "3":
          return PLAYERS.slice(5, 6);
        default:
          throw Error();
      }
    case 5:
      switch (agent) {
        case "0":
          return PLAYERS.slice(0, 2);
        case "1":
          return PLAYERS.slice(2, 3);
        case "2":
          return PLAYERS.slice(3, 4);
        case "3":
          return PLAYERS.slice(4, 5);
        case "4":
          return PLAYERS.slice(5, 6);
        default:
          throw Error();
      }
    case 6:
      switch (agent) {
        case "0":
          return PLAYERS.slice(0, 1);
        case "1":
          return PLAYERS.slice(1, 2);
        case "2":
          return PLAYERS.slice(2, 3);
        case "3":
          return PLAYERS.slice(3, 4);
        case "4":
          return PLAYERS.slice(4, 5);
        case "5":
          return PLAYERS.slice(5, 6);
        default:
          throw Error();
      }
    default:
      throw Error();
  }
}

export const PRUEBAS: Array<$Keys<Pruebas>> = [
  "consola",
  "digitos",
  "casillas",
  "colores",
  "poker",
  "sonidos",
];
